import React, { FC, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, query, where, orderBy, limit,
         doc, getDoc } from 'firebase/firestore';
import { UserAuth } from '../../AuthContext';
import { useTDTier } from '../../hooks/useTDTier';
import TierGate from '../../components/TierGate/TierGate';
import { Box, Flex, Text, Heading, Spinner, Center, Avatar } from '@chakra-ui/react';
import PoweredByFooter from '../../components/PoweredByFooter/PoweredByFooter';

const F = { ui:"'Rajdhani','Arial',sans-serif", mono:"'Share Tech Mono',monospace" };

const LICENSE_LABELS: Record<string, string> = {
  private_home_game: '🏠 Private Home Game',
  charitable: '❤️ Charitable / Fundraiser',
  pctds_sanctioned: '✅ PCTDS Sanctioned',
  licensed_venue: '🏢 Licensed Venue',
  casino_night: '🎰 Casino Night',
  corporate: '💼 Corporate Event',
  other: 'Other',
};

const TDRoomPage: FC = () => {
  const { canUseRoomPage, loading: tierLoading } = useTDTier();
  const { slug } = useParams<{ slug: string }>();
  const { user } = UserAuth();
  const navigate = useNavigate();

  const [venue, setVenue] = useState<any>(null);
  const [tdProfile, setTdProfile] = useState<any>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);
  const [pastEvents, setPastEvents] = useState<any[]>([]);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!slug) return;
      try {
        // Find venue by slug
        const venueSnap = await getDocs(
          query(collection(db, 'venues'), where('slug', '==', slug), limit(1))
        );
        if (venueSnap.empty) { setLoading(false); return; }
        const venueDoc = venueSnap.docs[0];
        const v = { id: venueDoc.id, ...venueDoc.data() };
        setVenue(v);
        setIsOwner(user?.uid === v.td_uid);
        setIsFollowing(v.followers?.includes(user?.uid));

        // TD profile
        if (v.td_uid) {
          const tdSnap = await getDoc(doc(db, 'users', v.td_uid));
          if (tdSnap.exists()) setTdProfile(tdSnap.data());
        }

        // Events
        const now = new Date().toISOString();
        const [upSnap, pastSnap] = await Promise.all([
          getDocs(query(collection(db, 'events'),
            where('admin_uid', '==', v.td_uid),
            where('start', '>=', now),
            orderBy('start', 'asc'), limit(10))),
          getDocs(query(collection(db, 'events'),
            where('admin_uid', '==', v.td_uid),
            where('start', '<', now),
            orderBy('start', 'desc'), limit(10))),
        ]);
        setUpcomingEvents(upSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setPastEvents(pastSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // Build leaderboard from past events
        const playerStats: Record<string, { name: string; avatar: string; wins: number; itm: number; events: number }> = {};
        for (const ev of pastSnap.docs) {
          const d = ev.data();
          if (d.winner_uid && d.winner_name) {
            if (!playerStats[d.winner_uid]) playerStats[d.winner_uid] = { name: d.winner_name, avatar: d.winner_avatar ?? '', wins: 0, itm: 0, events: 0 };
            playerStats[d.winner_uid].wins++;
            playerStats[d.winner_uid].events++;
          }
        }
        const lb = Object.entries(playerStats)
          .map(([uid, s]) => ({ uid, ...s }))
          .sort((a, b) => b.wins - a.wins)
          .slice(0, 10);
        setLeaderboard(lb);

      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [slug, user?.uid]);

  if (loading) return <Center h="400px"><Spinner size="xl" color="teal.400" /></Center>;

  if (!venue) return (
    <Center h="500px" flexDirection="column" gap={4}>
      <Text style={{ fontSize: 48 }}>🃏</Text>
      <Text style={{ fontFamily: F.ui, fontSize: 18, color: '#6B7590' }}>Room not found</Text>
      <Box as="button" px={5} py={3} bg="#131720" border="1px solid #1E2330"
        borderRadius={8} fontSize="13px" color="#6B7590" fontFamily={F.ui}
        cursor="pointer" onClick={() => navigate('/find-a-game')}>
        Browse Games →
      </Box>
    </Center>
  );

  const primary = venue.brand_color_primary ?? '#19E6CF';
  const secondary = venue.brand_color_secondary ?? '#D4A017';
  const C = { bg:'#080A0E', card:'#131720', border:'#1E2330',
    white:'#F0F4FF', ink:'#C8D0E0', dim:'#6B7590' };

  const formatDate = (iso: string) => new Date(iso).toLocaleDateString('en-CA',
    { weekday:'short', month:'short', day:'numeric', hour:'numeric', minute:'2-digit' });

  if (!tierLoading && !canUseRoomPage) return (
    <TierGate allowed={false} feature="TD Room Page"
      requiredTier="pro" inline={false}><div /></TierGate>
  );

  return (
    <Box minH="100vh" bg={C.bg} fontFamily={F.ui}>

      {/* ── BRANDED HEADER ── */}
      <Box bg="#0A0A0A" borderBottom={`2px solid ${primary}`} px={4} py={4}>
        <Box maxW="900px" mx="auto">
          <Flex align="center" justify="space-between">
            <Flex align="center" gap={3}>
              {venue.logo_url ? (
                <Box w="48px" h="48px" borderRadius={8} overflow="hidden"
                  border={`2px solid ${primary}`}>
                  <img src={venue.logo_url} alt="logo"
                    style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                </Box>
              ) : (
                <Center w="48px" h="48px" borderRadius={8}
                  bg={`${primary}22`} border={`2px solid ${primary}`}>
                  <Text style={{ fontSize: 24 }}>🃏</Text>
                </Center>
              )}
              <Box>
                <Text style={{ fontFamily: F.ui, fontSize: 20, fontWeight: 700,
                  color: '#FFF', lineHeight: 1.2 }}>
                  {venue.name}
                </Text>
                {venue.city && (
                  <Text style={{ fontFamily: F.mono, fontSize: 10,
                    color: primary, letterSpacing: '0.15em' }}>
                    {venue.city}{venue.province ? `, ${venue.province}` : ''}
                    {venue.pctds_status === 'verified' ? ' · ✅ PCTDS VERIFIED' : ''}
                  </Text>
                )}
              </Box>
            </Flex>
            <Flex gap={2}>
              {user && !isOwner && (
                <Box as="button" px={4} py={2}
                  bg={isFollowing ? `${primary}22` : 'transparent'}
                  border={`1px solid ${isFollowing ? primary : '#1E2330'}`}
                  borderRadius={6} fontSize="12px" fontWeight="700"
                  color={isFollowing ? primary : '#6B7590'}
                  fontFamily={F.ui} cursor="pointer">
                  {isFollowing ? '✓ Following' : '+ Follow'}
                </Box>
              )}
              {isOwner && (
                <Box as="button" px={4} py={2}
                  bg="transparent" border={`1px solid ${primary}`}
                  borderRadius={6} fontSize="12px" fontWeight="700"
                  color={primary} fontFamily={F.ui} cursor="pointer"
                  onClick={() => navigate(`/venue/${venue.id}`)}>
                  Edit Venue →
                </Box>
              )}
            </Flex>
          </Flex>
        </Box>
      </Box>

      <Box maxW="900px" mx="auto" px={4} py={6}>

        {/* ── HOUSE RULES ── always on top ── */}
        {venue.house_rules && (
          <Box bg="#0D1118" border={`1px solid ${secondary}`}
            borderRadius={10} p={4} mb={6}
            style={{ borderLeftColor: secondary, borderLeftWidth: 4 }}>
            <Text style={{ fontFamily: F.mono, fontSize: 11, color: secondary,
              letterSpacing: '0.2em', marginBottom: 10 }}>
              🏠 HOUSE RULES
            </Text>
            <Box>
              {venue.house_rules.split('\n').map((rule: string, i: number) => rule.trim() && (
                <Text key={i} style={{ fontFamily: F.ui, fontSize: 14,
                  color: '#C8D0E0', lineHeight: 1.8 }}>
                  • {rule.trim()}
                </Text>
              ))}
            </Box>
            {venue.license_type && (
              <Box mt={3} pt={3} borderTop={`1px solid #1E2330`}>
                <Text style={{ fontFamily: F.mono, fontSize: 10,
                  color: '#6B7590', letterSpacing: '0.12em' }}>
                  {LICENSE_LABELS[venue.license_type] ?? venue.license_type}
                </Text>
              </Box>
            )}
          </Box>
        )}

        {/* ── VENUE INFO ── */}
        {(venue.parking || venue.food_drinks || venue.amenities) && (
          <Box bg="#131720" border="1px solid #1E2330" borderRadius={10}
            overflow="hidden" mb={6}>
            <Box bg="#0D1520" px={4} py={3} borderBottom="1px solid #1E2330">
              <Text style={{ fontFamily: F.mono, fontSize: 11, color: primary,
                letterSpacing: '0.2em' }}>VENUE INFO</Text>
            </Box>
            <Flex p={4} gap={6} flexWrap="wrap">
              {venue.parking && (
                <Box flex={1} minW="180px">
                  <Text style={{ fontFamily: F.mono, fontSize: 10, color: '#6B7590',
                    letterSpacing: '0.15em', marginBottom: 6 }}>🅿️ PARKING</Text>
                  <Text style={{ fontFamily: F.ui, fontSize: 13, color: '#C8D0E0',
                    lineHeight: 1.7 }}>{venue.parking}</Text>
                </Box>
              )}
              {venue.food_drinks && (
                <Box flex={1} minW="180px">
                  <Text style={{ fontFamily: F.mono, fontSize: 10, color: '#6B7590',
                    letterSpacing: '0.15em', marginBottom: 6 }}>🍕 FOOD & DRINKS</Text>
                  <Text style={{ fontFamily: F.ui, fontSize: 13, color: '#C8D0E0',
                    lineHeight: 1.7 }}>{venue.food_drinks}</Text>
                </Box>
              )}
              {venue.amenities && (
                <Box flex={1} minW="180px">
                  <Text style={{ fontFamily: F.mono, fontSize: 10, color: '#6B7590',
                    letterSpacing: '0.15em', marginBottom: 6 }}>✨ AMENITIES</Text>
                  <Text style={{ fontFamily: F.ui, fontSize: 13, color: '#C8D0E0',
                    lineHeight: 1.7 }}>{venue.amenities}</Text>
                </Box>
              )}
            </Flex>
          </Box>
        )}

        <Flex gap={4} flexWrap="wrap" align="flex-start">

          {/* ── LEFT COLUMN ── */}
          <Box flex={2} minW="300px">

            {/* Upcoming events */}
            {upcomingEvents.length > 0 && (
              <Box bg="#131720" border="1px solid #1E2330" borderRadius={10}
                overflow="hidden" mb={4}>
                <Box bg="#0D1520" px={4} py={3} borderBottom="1px solid #1E2330">
                  <Text style={{ fontFamily: F.mono, fontSize: 11, color: primary,
                    letterSpacing: '0.2em' }}>UPCOMING EVENTS</Text>
                </Box>
                {upcomingEvents.map((ev, i) => (
                  <Flex key={ev.id} px={4} py={3} justify="space-between" align="center"
                    borderBottom={i < upcomingEvents.length-1 ? '1px solid #1E2330' : 'none'}
                    cursor="pointer" onClick={() => navigate(`/event/${ev.id}`)}>
                    <Box>
                      <Text style={{ fontFamily: F.ui, fontSize: 15, fontWeight: 600,
                        color: '#F0F4FF', marginBottom: 2 }}>
                        {ev.name ?? 'Poker Event'}
                      </Text>
                      <Text style={{ fontFamily: F.ui, fontSize: 12, color: '#6B7590' }}>
                        {ev.start ? formatDate(ev.start) : ''}
                      </Text>
                      {ev.license_type && (
                        <Text style={{ fontFamily: F.mono, fontSize: 10,
                          color: '#6B7590', marginTop: 2 }}>
                          {LICENSE_LABELS[ev.license_type] ?? ev.license_type}
                        </Text>
                      )}
                    </Box>
                    <Flex direction="column" align="flex-end" gap={1}>
                      {ev.buy_in > 0 && (
                        <Box px={2} py={1} bg="#0D1118" border="1px solid #1E2330"
                          borderRadius={4}>
                          <Text style={{ fontFamily: F.mono, fontSize:11, color:'#C8D0E0' }}>
                            ${ev.buy_in}
                          </Text>
                        </Box>
                      )}
                      <Text style={{ fontFamily: F.mono, fontSize: 10, color: primary }}>
                        View →
                      </Text>
                    </Flex>
                  </Flex>
                ))}
              </Box>
            )}

            {/* Past results */}
            {pastEvents.length > 0 && (
              <Box bg="#131720" border="1px solid #1E2330" borderRadius={10}
                overflow="hidden" mb={4}>
                <Box bg="#0D1118" px={4} py={3} borderBottom="1px solid #1E2330">
                  <Text style={{ fontFamily: F.mono, fontSize: 11, color: '#6B7590',
                    letterSpacing: '0.2em' }}>RECENT RESULTS</Text>
                </Box>
                {pastEvents.slice(0, 5).map((ev, i) => (
                  <Flex key={ev.id} px={4} py={3} justify="space-between" align="center"
                    borderBottom={i < Math.min(pastEvents.length, 5)-1 ? '1px solid #1E2330' : 'none'}>
                    <Box>
                      <Text style={{ fontFamily: F.ui, fontSize: 14, color: '#C8D0E0' }}>
                        {ev.name ?? 'Poker Event'}
                      </Text>
                      <Text style={{ fontFamily: F.ui, fontSize: 11, color: '#6B7590' }}>
                        {ev.start ? new Date(ev.start).toLocaleDateString('en-CA',
                          { month:'short', day:'numeric', year:'numeric' }) : ''}
                        {ev.attendee_count ? ` · ${ev.attendee_count} players` : ''}
                      </Text>
                    </Box>
                    <Flex direction="column" align="flex-end" gap={1}>
                      {ev.winner_name && (
                        <Text style={{ fontFamily: F.mono, fontSize: 11,
                          color: secondary }}>🏆 {ev.winner_name}</Text>
                      )}
                      {ev.prize_pool > 0 && (
                        <Text style={{ fontFamily: F.mono, fontSize: 10, color: '#6B7590' }}>
                          ${ev.prize_pool.toLocaleString()} pool
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                ))}
              </Box>
            )}
          </Box>

          {/* ── RIGHT COLUMN ── */}
          <Box flex={1} minW="220px">

            {/* TD info */}
            {tdProfile && (
              <Box bg="#131720" border="1px solid #1E2330" borderRadius={10} p={4} mb={4}>
                <Text style={{ fontFamily: F.mono, fontSize: 10, color: '#6B7590',
                  letterSpacing: '0.15em', marginBottom: 12 }}>TOURNAMENT DIRECTOR</Text>
                <Flex align="center" gap={3} cursor="pointer"
                  onClick={() => navigate(`/passport/${venue.td_uid}`)}>
                  <Avatar size="md" src={tdProfile.avatar_url}
                    name={tdProfile.display_name} />
                  <Box>
                    <Text style={{ fontFamily: F.ui, fontSize: 15, fontWeight: 700,
                      color: '#F0F4FF' }}>
                      {tdProfile.display_name ?? tdProfile.alias ?? 'TD'}
                    </Text>
                    <Text style={{ fontFamily: F.mono, fontSize: 10, color: primary }}>
                      View Passport →
                    </Text>
                  </Box>
                </Flex>
                <Flex gap={4} mt={4} pt={3} borderTop="1px solid #1E2330">
                  <Box textAlign="center">
                    <Text style={{ fontFamily: F.ui, fontSize: 20, fontWeight: 700,
                      color: '#F0F4FF' }}>{venue.event_count ?? 0}</Text>
                    <Text style={{ fontFamily: F.mono, fontSize: 9, color: '#6B7590',
                      letterSpacing: '0.1em' }}>EVENTS</Text>
                  </Box>
                  <Box textAlign="center">
                    <Text style={{ fontFamily: F.ui, fontSize: 20, fontWeight: 700,
                      color: secondary }}>
                      {venue.avg_rating > 0 ? `★${venue.avg_rating?.toFixed(1)}` : '—'}
                    </Text>
                    <Text style={{ fontFamily: F.mono, fontSize: 9, color: '#6B7590',
                      letterSpacing: '0.1em' }}>RATING</Text>
                  </Box>
                  <Box textAlign="center">
                    <Text style={{ fontFamily: F.ui, fontSize: 20, fontWeight: 700,
                      color: '#F0F4FF' }}>{venue.followers?.length ?? 0}</Text>
                    <Text style={{ fontFamily: F.mono, fontSize: 9, color: '#6B7590',
                      letterSpacing: '0.1em' }}>FOLLOWERS</Text>
                  </Box>
                </Flex>
              </Box>
            )}

            {/* Leaderboard */}
            {leaderboard.length > 0 && (
              <Box bg="#131720" border="1px solid #1E2330" borderRadius={10}
                overflow="hidden" mb={4}>
                <Box bg="#1A0F00" px={4} py={3} borderBottom="1px solid #1E2330">
                  <Text style={{ fontFamily: F.mono, fontSize: 11, color: secondary,
                    letterSpacing: '0.2em' }}>🏆 LEADERBOARD</Text>
                </Box>
                {leaderboard.map((p, i) => (
                  <Flex key={p.uid} px={4} py={2} align="center" gap={3}
                    borderBottom={i < leaderboard.length-1 ? '1px solid #1E2330' : 'none'}>
                    <Text style={{ fontFamily: F.mono, fontSize: 12,
                      color: i === 0 ? secondary : '#6B7590', width: 20 }}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i+1}.`}
                    </Text>
                    <Avatar size="xs" src={p.avatar} name={p.name} />
                    <Text style={{ fontFamily: F.ui, fontSize: 13, color: '#C8D0E0',
                      flex: 1 }}>{p.name}</Text>
                    <Text style={{ fontFamily: F.mono, fontSize: 11, color: secondary }}>
                      {p.wins}W
                    </Text>
                  </Flex>
                ))}
              </Box>
            )}

            {/* Social links */}
            {(venue.social?.facebook || venue.social?.instagram || venue.social?.discord) && (
              <Box bg="#131720" border="1px solid #1E2330" borderRadius={10} p={4}>
                <Text style={{ fontFamily: F.mono, fontSize: 10, color: '#6B7590',
                  letterSpacing: '0.15em', marginBottom: 10 }}>CONNECT</Text>
                <Flex direction="column" gap={2}>
                  {venue.social?.facebook && (
                    <Box as="button" px={3} py={2} bg="#0D1118" border="1px solid #1E2330"
                      borderRadius={6} fontSize="12px" fontWeight="700" color="#C8D0E0"
                      fontFamily={F.ui} cursor="pointer" textAlign="left"
                      onClick={() => window.open(venue.social.facebook, '_blank')}>
                      Facebook →
                    </Box>
                  )}
                  {venue.social?.instagram && (
                    <Box as="button" px={3} py={2} bg="#0D1118" border="1px solid #1E2330"
                      borderRadius={6} fontSize="12px" fontWeight="700" color="#C8D0E0"
                      fontFamily={F.ui} cursor="pointer" textAlign="left"
                      onClick={() => window.open(venue.social.instagram, '_blank')}>
                      Instagram →
                    </Box>
                  )}
                  {venue.social?.discord && (
                    <Box as="button" px={3} py={2} bg="#0D1118" border="1px solid #1E2330"
                      borderRadius={6} fontSize="12px" fontWeight="700" color="#C8D0E0"
                      fontFamily={F.ui} cursor="pointer" textAlign="left"
                      onClick={() => window.open(venue.social.discord, '_blank')}>
                      Discord →
                    </Box>
                  )}
                </Flex>
              </Box>
            )}
          </Box>
        </Flex>
      </Box>

      {/* Powered by footer */}
      <PoweredByFooter />
    </Box>
  );
};

export default TDRoomPage;
